import { Component } from '@theme/component'
import { useGsap } from '@theme/gsap'

const SearchTypedConfig = {
  attributes: {
    delay: 'data-delay',
    firstText: 'data-first-text',
    lastText: 'data-last-text'
  },
  classes: {
    blink: 'blink',
    cursor: 'typed-cursor'
  },
  events: {
    dialogClose: 'dialog:close',
    dialogOpen: 'dialog:open'
  },
  selectors: {
    dialogHost: 'use-dialog'
  },
  timings: {
    swapMilliseconds: 600,
    typeInSeconds: 1,
    typeOutSeconds: 0.25
  }
}

class SearchTyped extends Component {
  connectedCallback() {
    super.connectedCallback()
    this.initializeRuntimeState()
    this.connectSearchTrigger()
  }

  initializeRuntimeState() {
    this.handleIntersect = this.handleIntersect.bind(this)
    this.handleDialogOpen = this.handleDialogOpen.bind(this)
    this.handleDialogClose = this.handleDialogClose.bind(this)
    this.reducedMotionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    )
    this.dialogHost = this.closest(SearchTypedConfig.selectors.dialogHost)
    this.isRunning = false
    this.sequenceRequestId = 0
  }

  connectSearchTrigger() {
    if (this.dialogHost) {
      this.bindDialogHost()
      return
    }

    this.observeVisibility()
  }

  bindDialogHost() {
    this.dialogHost.addEventListener(
      SearchTypedConfig.events.dialogOpen,
      this.handleDialogOpen
    )
    this.dialogHost.addEventListener(
      SearchTypedConfig.events.dialogClose,
      this.handleDialogClose
    )
  }

  observeVisibility() {
    this.intersectionObserver?.disconnect()
    this.intersectionObserver = new IntersectionObserver(this.handleIntersect)
    this.intersectionObserver.observe(this)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.intersectionObserver?.disconnect()
    this.intersectionObserver = null
    this.dialogHost?.removeEventListener(
      SearchTypedConfig.events.dialogOpen,
      this.handleDialogOpen
    )
    this.dialogHost?.removeEventListener(
      SearchTypedConfig.events.dialogClose,
      this.handleDialogClose
    )
    this.gsapContext?.revert()
    this.resetCursor()
  }

  handleIntersect(entries) {
    if (!entries[0]?.isIntersecting) return
    this.intersectionObserver.disconnect()
    this.startSequence()
  }

  handleDialogOpen() {
    this.startSequence()
  }

  handleDialogClose() {
    this.sequenceRequestId += 1
    this.resetCursor()
    this.textContent = ''
    this.style.removeProperty('clip-path')
    this.isRunning = false
  }

  get startDelay() {
    const raw = this.getAttribute(SearchTypedConfig.attributes.delay)
    return raw ? Number.parseFloat(raw) : 0
  }

  get firstText() {
    return this.getAttribute(SearchTypedConfig.attributes.firstText) ?? ''
  }

  get lastText() {
    return this.getAttribute(SearchTypedConfig.attributes.lastText) ?? ''
  }

  async startSequence() {
    if (this.isRunning) return
    const requestId = this.beginSequence()

    if (this.reducedMotionQuery.matches) {
      this.finishStaticSequence()
      return
    }

    const scope = await useGsap(this)
    if (!this.prepareSequenceRuntime(requestId, scope)) return

    await this.playTypedSequence()
  }

  beginSequence() {
    this.isRunning = true
    return this.nextSequenceRequestId()
  }

  finishStaticSequence() {
    this.textContent = this.lastText
    this.isRunning = false
  }

  prepareSequenceRuntime(requestId, scope) {
    if (!this.isCurrentSequence(requestId)) {
      this.isRunning = false
      return false
    }
    if (!scope) {
      this.finishStaticSequence()
      return false
    }

    this.gsapContext?.revert()
    this.gsap = scope.gsap
    this.gsapContext = scope.context
    this.insertCursor()
    return true
  }

  async playTypedSequence() {
    try {
      await this.typeIn(this.firstText, this.startDelay)
      await wait(SearchTypedConfig.timings.swapMilliseconds)
      await this.eraseOut()
      await this.typeIn(this.lastText, 0)
    } finally {
      this.isRunning = false
    }
  }

  nextSequenceRequestId() {
    this.sequenceRequestId += 1
    return this.sequenceRequestId
  }

  isCurrentSequence(requestId) {
    if (!this.isConnected) return false
    if (requestId !== this.sequenceRequestId) return false

    return true
  }

  applyReveal(progress) {
    const hiddenPercent = (1 - progress) * 100
    this.style.clipPath = `inset(0 ${hiddenPercent}% 0 0)`
    const cursorX = -(1 - progress) * this.revealWidth
    this.cursor?.style.setProperty('transform', `translateX(${cursorX}px)`)
  }

  async typeIn(text, delay) {
    this.innerHTML = text
    this.style.overflow = 'hidden'
    this.style.display = 'flex'
    this.style.width = 'auto'
    this.revealWidth = this.scrollWidth
    this.applyReveal(0)

    await this.tweenReveal({
      delay,
      duration: SearchTypedConfig.timings.typeInSeconds,
      ease: 'power2.out',
      from: 0,
      to: 1
    })

    this.cursor?.classList.add(SearchTypedConfig.classes.blink)
  }

  async eraseOut() {
    this.cursor?.classList.remove(SearchTypedConfig.classes.blink)

    await this.tweenReveal({
      duration: SearchTypedConfig.timings.typeOutSeconds,
      ease: 'power2.in',
      from: 1,
      to: 0
    })
  }

  tweenReveal({ delay = 0, duration, ease, from, to }) {
    return new Promise((resolve) => {
      this.gsapContext.add(() => {
        const state = { progress: from }
        this.gsap.to(state, {
          delay,
          duration,
          ease,
          onComplete: resolve,
          onUpdate: () => this.applyReveal(state.progress),
          progress: to
        })
      })
    })
  }

  insertCursor() {
    if (this.cursor || !this.parentElement) return
    this.cursor = document.createElement('span')
    this.cursor.className = SearchTypedConfig.classes.cursor
    this.cursor.setAttribute('aria-hidden', 'true')
    this.cursor.textContent = '|'
    this.parentElement.insertBefore(this.cursor, this.nextSibling)
  }

  resetCursor() {
    this.cursor?.remove()
    this.cursor = null
  }
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

if (!customElements.get('ui-search-typed')) {
  customElements.define('ui-search-typed', SearchTyped)
}
